import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, KeyRound, Link2, Loader2, ShieldCheck, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";
import { landingForUser } from "@/lib/roleRedirect";

const PROVIDER_LABELS = { google: "Google", facebook: "Facebook" };
const LINK_ERRORS = {
  identity_in_use: "That social account is already connected to a different AWARE account.",
  invalid_state: "That connection attempt expired — please try again.",
  provider_error: "The provider is temporarily unavailable. Please try again shortly.",
  cancelled: "Connection was cancelled.",
  not_configured: "That provider isn't available right now.",
};

export default function AccountSignInMethods() {
  const { user } = useAuth();
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/auth/sign-in-methods").then(({ data }) => setMethods(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const linked = params.get("linked");
    const err = params.get("link_error");
    if (linked) toast.success(`${PROVIDER_LABELS[linked] || linked} connected to your AWARE account`);
    if (err) toast.error(LINK_ERRORS[err] || "Connection failed — please try again.");
    if (linked || err) setParams({}, { replace: true });
  }, [params, setParams]);

  const connect = async (provider) => {
    setBusy(provider);
    try {
      const { data } = await api.post(`/auth/social/${provider}/link-start`);
      window.location.assign(data.url);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail === "recent_login_required"
        ? "For security, please sign out and sign in again before connecting a new sign-in method."
        : formatApiError(detail));
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${PROVIDER_LABELS[provider]} from your AWARE account? You'll still be able to sign in with your remaining method(s).`)) return;
    setBusy(provider);
    try {
      await api.delete(`/auth/social/${provider}`);
      toast.success(`${PROVIDER_LABELS[provider]} disconnected`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail === "last_method"
        ? "You can't disconnect your only sign-in method — set a password or connect another provider first."
        : formatApiError(detail));
    } finally {
      setBusy(null);
    }
  };

  const rows = methods && [
    {
      key: "password",
      label: "AWARE email & password",
      icon: KeyRound,
      connected: methods.password,
      detail: methods.password ? user?.email : "No password set on this account",
      action: methods.password
        ? <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide" data-testid="method-password-status">Active</span>
        : <Link to="/forgot-password" data-testid="method-password-set-link" className="text-xs font-semibold text-cyan-700 hover:underline">Set a password →</Link>,
    },
    ...["google", "facebook"].map((p) => ({
      key: p,
      label: PROVIDER_LABELS[p],
      icon: Link2,
      connected: methods[p]?.connected,
      hidden: !methods.enabled?.[p] && !methods[p]?.connected,
      detail: methods[p]?.connected
        ? (methods[p].email || "Connected")
        : "Not connected",
      action: methods[p]?.connected ? (
        <Button data-testid={`method-${p}-disconnect`} size="sm" variant="outline" disabled={busy === p}
          className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
          onClick={() => disconnect(p)}>
          <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect
        </Button>
      ) : (
        <Button data-testid={`method-${p}-connect`} size="sm" variant="outline" disabled={busy === p}
          className="rounded-full" onClick={() => connect(p)}>
          {busy === p ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Connect</>}
        </Button>
      ),
    })),
  ].filter((r) => !r.hidden);

  return (
    <div data-testid="sign-in-methods-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          {user && (
            <Link to={landingForUser(user)} data-testid="sign-in-methods-back" className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to my dashboard
            </Link>
          )}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-cyan-600" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Sign-in methods</h1>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          These are the ways you can sign in to your AWARE account. Connecting Google or Facebook never changes what you can access — it's just another key to the same account.
        </p>

        <div className="mt-6 bg-white rounded-3xl border border-slate-200 shadow-sm divide-y divide-slate-100">
          {!rows && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {rows && rows.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.key} data-testid={`method-row-${r.key}`} className="flex items-center gap-4 p-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${r.connected ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
                  <div className="text-xs text-slate-500 truncate">{r.detail}</div>
                </div>
                {r.action}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-slate-500">
          You can't remove your last remaining sign-in method — this prevents accidental lockouts.
        </p>
      </main>
    </div>
  );
}
